import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid2, Typography } from "@mui/material";
import ChartDrillDownModalV2 from "../../BmsFaults/Analytics/ChartDrillDown/ChartDrillDownModalV2";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import DigitalTwinTableSearch from "../../../Components/DigitalTwinTableSearch";
import { formatDateRangeWithSmartYear } from "../../../Helper/DatePicker/DateFormatters";
import { getSmartAlertsList, smartAlertsListAction } from "../../../Api/SmartAlertsAnalyticsApi";
import { genericSearchSuggestions } from "../../../Api/DigitalTwinListApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { deviceSearchFilter } from "./drillDownFilters";
import {
  SMART_ALERTS_META_INFO_FIELDS,
  deriveMetaInfoFields,
  pickMetaInfoValues,
} from "../../../Helper/Analytics/tableMetaInfo";

// "Auto Resolved" or "Manually Resolved" → "AutoResolved" or "ManualResolved"
const RESOLUTION_LABEL_TO_API = {
  "Auto Resolved":      "AutoResolved",
  "Manually Resolved":  "ManualResolved",
};

const ResolutionDurationBreakdownDrillDownModal = ({
  open,
  onClose,
  selectedDataPoint,
  chartTitle,
  startDate,
  endDate,
  globalFilters = {},
}) => {
  const pagesContent = usePagesContent();
  const summaryMeta  = pagesContent?.metaInfo?.table?.resolutionDurationBreakdownSummaryTable;
  const vehiclesMeta = pagesContent?.metaInfo?.table?.resolutionDurationBreakdownVehiclesTable;

  const [level2SearchTerm, setLevel2SearchTerm] = useState({ selectedDevices: [], selectedSearchColumn: null });

  useEffect(() => {
    if (!open) setLevel2SearchTerm({ selectedDevices: [], selectedSearchColumn: null });
  }, [open]);

  const handleLevel2Search = useCallback((selectedDevices, selectedSearchColumn) => {
    setLevel2SearchTerm({ selectedDevices, selectedSearchColumn });
  }, []);

  const searchSuggestionsHandler = useCallback(
    (typedValue, _columnName, limit) => genericSearchSuggestions(typedValue, "licensePlateNo", limit),
    []
  );

  const faultName          = selectedDataPoint?.faultName || "";
  const currentGroupBy     = selectedDataPoint?.currentGroupBy || "originCity";
  const resolutionLabel    = selectedDataPoint?.resolutionType || "";
  const resolutionApiValue = RESOLUTION_LABEL_TO_API[resolutionLabel] || "AutoResolved";
  const selectedItemIds    = (selectedDataPoint?.selectedItems || [])
    .filter((i) => i.id !== "all")
    .map((i) => i.id);
  const selectedItemFilter = selectedItemIds.length > 0
    ? [{ name: currentGroupBy, values: selectedItemIds }]
    : [];

  const level1TableConfigs = summaryMeta ? {
    tableColumnsConfig: summaryMeta.columnsConfig,
    tableColumnOrder:   summaryMeta.columnOrdering,
    title: (dp) => dp?.faultName || "Resolution Duration Breakdown",
    emptyMessage: "No alerts found for the selected criteria",
  } : null;

  // What the vehicles table asks the API for, and reads back, follows the columns
  // it is configured to show. Memoised on the config so the callbacks below keep a
  // stable identity and do not refetch on every render.
  const level2MetaInfoFields = useMemo(
    () => deriveMetaInfoFields(vehiclesMeta?.columnsConfig, SMART_ALERTS_META_INFO_FIELDS),
    [vehiclesMeta]
  );

  const level2TableConfigs = vehiclesMeta ? {
    tableColumnsConfig: vehiclesMeta.columnsConfig,
    tableColumnOrder:   vehiclesMeta.columnOrdering,
    title: "Vehicles",
    emptyMessage: "No vehicles found for the selected fault",
  } : null;

  const getLevel1TableData = useCallback(async (_dp, pageNumber, sortBy) => {
    if (!startDate || !endDate || !faultName) return { data: [], total: 0 };

    const result = await getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select:    ["ResolvedAlertCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration"],
      groupBy:   [currentGroupBy],
      skipPagination: false,
      pageNum:        pageNumber || 1,
      pageCount:      10,
      ...(sortBy?.length ? { sortBy } : {}),
      filters: [
        { name: "alertDescription", values: [faultName]         },
        { name: "ResolutionType",   values: [resolutionApiValue] },
        ...selectedItemFilter,
      ],
    });

    if (result?.responseStatus?.code !== CODE.SUCCESS) return { data: [], total: 0 };

    const rows = (result.response?.data || []).map((row) => {
      const metrics = row.metricsArray || [];
      return {
        id:                    row.xLabel,
        xLabel:                row.xLabel,
        ResolvedAlertCount:    metrics.find((m) => m.type === "ResolvedAlertCount")?.value    ?? 0,
        avgResolutionDuration: metrics.find((m) => m.type === "avgResolutionDuration")?.value ?? 0,
        minResolutionDuration: metrics.find((m) => m.type === "minResolutionDuration")?.value ?? 0,
        maxResolutionDuration: metrics.find((m) => m.type === "maxResolutionDuration")?.value ?? 0,
      };
    });

    return { data: rows, total: result.response?.total ?? rows.length };
  }, [startDate, endDate, faultName, currentGroupBy, resolutionApiValue, selectedItemIds]);

  const getLevel2TableData = useCallback(async (level1Record, pageNumber, sortBy) => {
    if (!startDate || !endDate) return { data: [], total: 0 };

    const locationValue = level1Record?.xLabel;
    const { selectedDevices } = level2SearchTerm;
    const filters = [
      { name: "ResolutionType",   values: [resolutionApiValue] },
      ...(faultName     ? [{ name: "alertDescription", values: [faultName]     }] : []),
      ...(locationValue ? [{ name: currentGroupBy,     values: [locationValue] }] : []),
      ...deviceSearchFilter(selectedDevices),
    ];

    const result = await getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select:    ["ResolvedAlertCount"],
      groupBy:   ["batteryID"],
      metaInfo:  level2MetaInfoFields,
      skipPagination: false,
      pageNum:        pageNumber || 1,
      pageCount:      10,
      ...(sortBy?.length ? { sortBy } : {}),
      filters,
    });

    if (result?.responseStatus?.code !== CODE.SUCCESS) return { data: [], total: 0 };

    const rows = (result.response?.data || []).map((row) => {
      const metrics = row.metricsArray || [];
      return {
        id:                row.xLabel,
        ...pickMetaInfoValues(row, level2MetaInfoFields),
        ResolvedAlertCount: metrics.find((m) => m.type === "ResolvedAlertCount")?.value ?? 0,
      };
    });

    return { data: rows, total: result.response?.total ?? rows.length };
  }, [startDate, endDate, faultName, currentGroupBy, resolutionApiValue, level2SearchTerm, level2MetaInfoFields]);

  const buildCsvColumns = (tableColumns) =>
    tableColumns
      .filter((col) => col.dataIndex !== "actions")
      .map((col) => ({
        dataIndex:   col.dataIndex === "xLabel" || col.dataIndex === "id" ? "xLabel" : col.dataIndex,
        headerLabel: col.headerLabel || col.title,
      }));

  const applyLevel1TableAction = useCallback(async (rowSelectionKeys, actionType, selectAll, params) => {
    const { columns: tCols, emailTo, emailCc, subject, body } = params;
    const csvColumns = buildCsvColumns(tCols);
    const result = await smartAlertsListAction(globalFilters, {
      actionType:  actionType === "download" ? "download-csv" : "mail",
      startDate,
      endDate,
      select:      ["ResolvedAlertCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration"],
      groupBy:     [currentGroupBy],
      filters: [
        { name: "alertDescription", values: [faultName]         },
        { name: "ResolutionType",   values: [resolutionApiValue] },
        ...selectedItemFilter,
      ],
      selectAll:   selectAll || rowSelectionKeys.length === 0,
      resourceIDs: selectAll ? [] : rowSelectionKeys,
      fileName:    "resolution_duration_summary",
      columns:     csvColumns,
      columnOrder: csvColumns.map((c) => c.headerLabel),
      ...(actionType === "mail" ? { emailTo, emailCc, subject, body } : {}),
    });
    if (actionType === "download") return result?.response?.data || [];
  }, [globalFilters, startDate, endDate, faultName, currentGroupBy, resolutionApiValue, selectedItemIds]);

  const applyLevel2TableAction = useCallback(async (rowSelectionKeys, actionType, selectAll, params, level1Record) => {
    const { columns: tCols, emailTo, emailCc, subject, body } = params;
    const csvColumns = buildCsvColumns(tCols);
    const locationValue = level1Record?.xLabel;
    const { selectedDevices } = level2SearchTerm;
    const filters = [
      { name: "ResolutionType",   values: [resolutionApiValue] },
      ...(faultName     ? [{ name: "alertDescription", values: [faultName]     }] : []),
      ...(locationValue ? [{ name: currentGroupBy,     values: [locationValue] }] : []),
      ...deviceSearchFilter(selectedDevices),
    ];
    const result = await smartAlertsListAction(globalFilters, {
      actionType:  actionType === "download" ? "download-csv" : "mail",
      startDate,
      endDate,
      select:      ["ResolvedAlertCount"],
      groupBy:     ["batteryID"],
      metaInfo:    level2MetaInfoFields,
      filters,
      selectAll:   selectAll || rowSelectionKeys.length === 0,
      resourceIDs: selectAll ? [] : rowSelectionKeys,
      fileName:    "resolution_duration_vehicles",
      columns:     csvColumns,
      columnOrder: csvColumns.map((c) => c.headerLabel),
      ...(actionType === "mail" ? { emailTo, emailCc, subject, body } : {}),
    });
    if (actionType === "download") return result?.response?.data || [];
  }, [globalFilters, startDate, endDate, faultName, currentGroupBy, resolutionApiValue, level2SearchTerm, level2MetaInfoFields]);

  const vehiclesSearchBar = vehiclesMeta?.searchBar;
  const level2SearchComponent = vehiclesSearchBar?.isVisible ? (
    <DigitalTwinTableSearch
      onSearch={handleLevel2Search}
      containerWidth="80%"
      searchableFields={(vehiclesSearchBar.searchableFields || []).map((f) => ({
        key: f.key,
        title: f.label,
      }))}
      searchSuggestionsHandler={searchSuggestionsHandler}
    />
  ) : null;

  if (!level1TableConfigs) return null;

  const base           = chartTitle || "Battery System Alert Avg. Resolution Duration Breakdown";
  const groupByLabel   = selectedDataPoint?.groupBy?.label || "";
  const selectedItemLabel = (() => {
    const items = selectedDataPoint?.selectedItems || [];
    const nonAll = items.filter((i) => i.id !== "all");
    if (!nonAll.length || items.some((i) => i.id === "all")) return "All";
    if (nonAll.length === 1) return nonAll[0].label || "";
    return `${nonAll.length} selected`;
  })();
  const dateRange      = formatDateRangeWithSmartYear(startDate, endDate);

  const modalTitle = () => (
    <Grid2 container gap={1} alignItems="center">
      <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
        {`${base}:`}{" "}
        {[groupByLabel, selectedItemLabel, resolutionLabel].filter(Boolean).join(" / ")}
        <Typography component="span" variant="fw500s16c000000" sx={{ lineHeight: "28px" }}>
          {faultName ? ` / ${faultName}` : ""}
        </Typography>
      </Typography>
      {dateRange && (
        <>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{"•"}</Typography>
          <Typography variant="fw500s14c686868" sx={{ lineHeight: "20px" }}>{dateRange}</Typography>
        </>
      )}
    </Grid2>
  );

  const level2Title = (record) => (
    <Grid2 container gap={1} alignItems="center">
      <Typography variant="fw400s14c8F8F8F" sx={{ lineHeight: "28px" }}>
        {record?.xLabel || "Details"}
        <Typography component="span" variant="fw400s14c000000" sx={{ lineHeight: "28px" }}>
          {" / Details"}
        </Typography>
      </Typography>
    </Grid2>
  );

  return (
    <ChartDrillDownModalV2
      open={open}
      onClose={onClose}
      disableColumnSettings={false}
      alertsSubTab="platform-alerts"
      title={modalTitle}
      selectedDataPoint={selectedDataPoint}
      tableConfigs={level1TableConfigs}
      getTableData={getLevel1TableData}
      applyTableAction={applyLevel1TableAction}
      getCSVFileName={() => "resolution_duration_summary"}
      level2TableConfigs={level2TableConfigs}
      level2GetTableData={getLevel2TableData}
      level2ApplyTableAction={applyLevel2TableAction}
      level2GetCSVFileName={() => "resolution_duration_vehicles"}
      level2SearchComponent={level2SearchComponent}
      level2Title={level2Title}
    />
  );
};

export default ResolutionDurationBreakdownDrillDownModal;
